/**
 * The title (docs/spec/04 §6): the STARHIVE marquee dropping into its frame of chasing bulbs, the
 * hive on parade, the starfield, the menu with the Lancer as its cursor. The first visit of a page
 * load asks for a key first (which unlocks audio); twenty idle seconds start the attract demo.
 */

import { GAME_NAME } from '../../app/brand';
import type { Screen } from '../../render/screen';
import { drawSprite } from '../../render/sprites';
import { UiScene } from '../kit/base';
import { isModifier, notOurs } from '../kit/input';
import { drawLogo, drawMarquee } from '../kit/logo';
import { dropDone, logoWidth } from '../kit/logoModel';
import type { MenuLook } from '../kit/menu';
import { drawMenu } from '../kit/menu';
import type { Rect } from '../kit/menuModel';
import { moveSelection, rowAt } from '../kit/menuModel';
import { commas } from '../kit/text';
import { ACCENT, blink, CX, HEADING, INK, LEFT, MUTED, RIGHT, row, SELECTED, W, wingFrame } from '../kit/theme';
import type { KeyInput, MenuAction, PointerInput, SceneHost } from '../scene';
import { DEMO_ROUTE, IDLE_MS, PARADE, paradeX, TITLE_ITEMS, versionLabel } from './titleMenu';

/** The first key of this page load has been pressed. */
let pressed = false;
/** The menu remembers its place for the page's life. */
let lastSel = 0;

const FRAME: Rect = { x: 12, y: 28, w: 200, h: 48 };
const LOGO_Y = 38;
const PARADE_Y = 94;
const MENU: MenuLook = { top: 116, rowH: 16, x: 24, w: 176, cx: CX, slide: true, marker: 'ship' };
const ABOUT_Y = row(30);
const FOOT_Y = row(34);

export class TitleScene extends UiScene {
  protected readonly heading = '';
  /** Chosen by hand: nothing can play until the first key unlocks audio. */
  protected override readonly track = null;
  protected override readonly veil = 0;
  private sel = lastSel;
  private waiting: boolean;
  private idle = 0;
  private dropMs: number | null = 0;
  /** When the menu last appeared, for its slide-in. */
  private menuAt = 0;

  constructor(host: SceneHost, skipPress: boolean) {
    super(host);
    this.waiting = !pressed && !skipPress;
  }

  override enter(): void {
    super.enter();
    this.idle = 0;
    this.menuAt = 0;
    if (!this.waiting) this.services.audio.music('title');
  }

  protected override describe(): string {
    if (this.waiting) return `${GAME_NAME}. Press any key to start.`;
    return `${GAME_NAME}. Main menu. ${TITLE_ITEMS[this.sel]?.label ?? ''}`;
  }

  /** The stars rush while the letters fall, as they do through a stage's intro. */
  protected override warp(): number {
    return this.dropMs === null ? 1 : 4;
  }

  protected override tick(dt: number): void {
    this.idle += dt;
    if (this.dropMs !== null) {
      this.dropMs += dt;
      if (dropDone(this.dropMs, [...GAME_NAME].length)) this.dropMs = null;
    }
    if (this.idle >= IDLE_MS) {
      this.idle = 0;
      this.host.go(DEMO_ROUTE);
    }
  }

  protected paint(screen: Screen): void {
    screen.text('HIGH SCORE', CX, row(0), HEADING, 'center');
    screen.text(commas(this.services.scores.best('supernova')), CX, row(1), INK, 'center');
    drawMarquee(screen, FRAME, this.ms);
    drawLogo(screen, GAME_NAME, Math.floor((W - logoWidth(GAME_NAME)) / 2), LOGO_Y, this.ms, this.dropMs);
    PARADE.forEach((id, i) => drawSprite(screen, id, wingFrame(this.ms), paradeX(i, this.ms, CX), PARADE_Y));
    if (this.waiting) this.paintPress(screen);
    else {
      drawMenu(screen, TITLE_ITEMS, this.sel, MENU, this.ms - this.menuAt);
      screen.text(TITLE_ITEMS[this.sel]?.about ?? '', CX, ABOUT_Y, ACCENT, 'center');
    }
    screen.text('© 2026', LEFT, FOOT_Y, MUTED);
    screen.text(versionLabel(this.services.version), RIGHT, FOOT_Y, MUTED, 'right');
  }

  private paintPress(screen: Screen): void {
    if (blink(this.ms, 900)) screen.text('PRESS ANY KEY', CX, row(18), SELECTED, 'center');
    screen.text('OR CLICK, OR TAP', CX, row(20), MUTED, 'center');
    // The Lancer waits where it will fly.
    drawSprite(screen, 'lancer', 0, CX, row(29));
  }

  /** The first input of the page load: audio may start now. */
  private wake(): void {
    this.idle = 0;
    if (pressed) return;
    pressed = true;
    this.services.audio.unlock();
    if (!this.waiting) this.services.audio.music('title');
  }

  private press(): void {
    this.wake();
    this.waiting = false;
    this.menuAt = this.ms;
    this.services.audio.music('title');
    this.sound('select');
    this.say(this.describe());
  }

  private move(delta: number): void {
    const next = moveSelection(TITLE_ITEMS, this.sel, delta);
    if (next !== this.sel) this.select(next);
  }

  private select(i: number): void {
    this.sel = i;
    lastSel = i;
    this.sound('move');
    this.say(TITLE_ITEMS[i]?.label ?? '');
  }

  private choose(i: number): void {
    const item = TITLE_ITEMS[i];
    if (!item) return;
    this.sel = i;
    lastSel = i;
    this.sound('select');
    if (item.replace === true) this.host.go(item.route);
    else this.host.push(item.route);
  }

  keydown(k: KeyInput): boolean {
    // "Any key" is not reload, the developer tools or a bare modifier: those stay the browser's.
    if (notOurs(k) || isModifier(k.code)) return false;
    if (this.waiting) {
      this.press();
      return true;
    }
    this.wake();
    return false;
  }

  protected onAction(a: MenuAction): void {
    if (this.waiting) return this.press();
    this.wake();
    if (a === 'up') this.move(-1);
    else if (a === 'down') this.move(1);
    else if (a === 'confirm') this.choose(this.sel);
  }

  /** Esc on the title has nowhere to go. */
  protected override back(): void {
    if (this.waiting) this.press();
    else this.wake();
  }

  protected override onPointer(p: PointerInput): void {
    if (this.waiting) {
      if (p.kind === 'down') this.press();
      else this.idle = 0;
      return;
    }
    if (p.kind === 'move') this.idle = 0;
    else this.wake();
    if (p.kind === 'wheel') return this.move(p.delta);
    const i = rowAt(MENU, TITLE_ITEMS.length, p.x, p.y);
    if (i < 0) return;
    if (p.kind === 'move' && i !== this.sel) this.select(i);
    else if (p.kind === 'down' && p.button === 0) this.choose(i);
  }
}

/** Tests: forget that a key was pressed and where the menu was. */
export function resetTitleMemory(): void {
  pressed = false;
  lastSel = 0;
}
